import { useEffect, useRef, useState } from 'react'

interface TypeSwapProps {
  /** Word shown first, then backspaced away. */
  from: string
  /** Word typed out — also the value rendered server-side and with JS disabled. */
  to: string
  /** Leading chars rendered without the brand emphasis, e.g. "in " (kept normal
   *  while the rest gets the headline's <em> treatment). Must prefix both words. */
  plainPrefix?: string
  /** ms to hold on `from` before deleting. */
  holdMs?: number
}

type Phase = 'static' | 'running' | 'done'

// Render `text` with its first `prefixLen` chars plain and the rest emphasized,
// matching the headline's <em> styling. Handles partial strings mid-animation.
function Word({ text, prefixLen }: { text: string; prefixLen: number }) {
  if (prefixLen <= 0) return <em>{text}</em>
  return (
    <>
      {text.slice(0, prefixLen)}
      <em>{text.slice(prefixLen)}</em>
    </>
  )
}

/**
 * One-shot typewriter swap for an emphasized phrase in a headline.
 *
 * Renders `to` immediately so SSG / no-JS / crawlers see the real value. On
 * mount it plays: show `from` → hold → backspace → type `to`. The initial swap
 * to `from` lands before the hero's reveal fade finishes, so there's no visible
 * flash of `to`. Respects prefers-reduced-motion (stays on `to`, no caret).
 *
 * Centering: a hidden sizer reserves the wider word's width ONLY while animating
 * (so the headline doesn't reflow per keystroke), and the live text is centered
 * over it. At rest there's no sizer, so the span is exactly `to`'s width and the
 * headline centers normally.
 */
export default function TypeSwap({ from, to, plainPrefix = '', holdMs = 1100 }: TypeSwapProps) {
  const [text, setText] = useState(to)
  const [phase, setPhase] = useState<Phase>('static')
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return

    const timers: ReturnType<typeof setTimeout>[] = []
    const at = (ms: number, fn: () => void) => timers.push(setTimeout(fn, ms))

    const DELETE_MS = 50
    const TYPE_MS = 75
    const GAP_MS = 260

    // Swap to the "before" word straight away — hidden under the reveal fade.
    setPhase('running')
    setText(from)

    let t = holdMs
    for (let i = from.length - 1; i >= 0; i--) {
      const s = from.slice(0, i)
      at(t, () => setText(s))
      t += DELETE_MS
    }
    t += GAP_MS
    for (let i = 1; i <= to.length; i++) {
      const s = to.slice(0, i)
      at(t, () => setText(s))
      t += TYPE_MS
    }
    at(t, () => setPhase('done'))

    return () => timers.forEach(clearTimeout)
  }, [from, to, holdMs])

  const sizer = from.length >= to.length ? from : to
  const prefixLen = plainPrefix.length

  return (
    <span className={`typeswap${phase === 'running' ? ' is-running' : ''}`}>
      <span className="sr-only">{to}</span>
      {phase === 'running' && (
        <span className="typeswap-sizer" aria-hidden="true">
          <Word text={sizer} prefixLen={prefixLen} />
        </span>
      )}
      <span className="typeswap-ink" aria-hidden="true">
        <Word text={text} prefixLen={prefixLen} />
        {phase !== 'static' && (
          <span className={`typeswap-caret${phase === 'done' ? ' is-done' : ''}`} />
        )}
      </span>
    </span>
  )
}
